import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUser, listGroups, searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { Link } from 'react-router-dom';
import { Label } from 'semantic-ui-react';
import List from '../List';
import jwtDecode from 'jwt-decode';
import { FormattedDate } from '@plone/volto/components';

const VERIFIED_LOOKBACK_DAYS = 28;

const DashboardWikis = () => {
  const dispatch = useDispatch();
  // get wikis by catalog search
  const verifiedWikiItems =
    useSelector((state) => state?.search?.subrequests?.verifiedWikis?.items) ||
    [];
  const discussionWikiItems =
    useSelector(
      (state) => state?.search?.subrequests?.discussionWikis?.items,
    ) || [];
  const releaseControlWikiItems =
    useSelector(
      (state) => state?.search?.subrequests?.releaseControlWikis?.items,
    ) || [];
  // get all groups
  const groups = useSelector((state) => state.groups?.groups ?? []);
  // get current user information
  const userId = useSelector((state) =>
    state.userSession?.token ? jwtDecode(state.userSession.token).sub : '',
  );
  const user = useSelector((state) => state.users?.user || {});

  React.useEffect(() => {
    dispatch(listGroups());
    dispatch(getUser(userId));
  }, [dispatch, userId]);

  React.useEffect(() => {
    const since = new Date();
    since.setDate(since.getDate() - VERIFIED_LOOKBACK_DAYS);
    dispatch(
      searchContent(
        '/wiki',
        {
          'path.depth': 1,
          portal_type: ['Wiki'],
          metadata_fields: ['departments', 'modified'],
          sort_on: 'modified',
          sort_order: 'reverse',
          review_state: 'verified',
          b_size: 50,
          'modified.query': since.toISOString(),
          'modified.range': 'min',
        },
        'verifiedWikis',
      ),
    );
  }, [dispatch]);

  React.useEffect(() => {
    if (user.fachbereich && user.fachbereich.length > 0) {
      const currentUserDepartments = user?.fachbereich?.map((obj) => obj.id);

      dispatch(
        searchContent(
          '/wiki',
          {
            'path.depth': 1,
            portal_type: ['Wiki'],
            review_state: 'release_control',
            departments: currentUserDepartments,
            metadata_fields: ['departments', 'modified'],
            b_size: 50,
            sort_on: 'modified',
            sort_order: 'reverse',
          },
          'releaseControlWikis',
        ),
      );
    }
  }, [dispatch, user.fachbereich]);

  React.useEffect(() => {
    if (user.fachbereich && user.fachbereich.length > 0) {
      const currentUserDepartments = user?.fachbereich?.map((obj) => obj.id);

      dispatch(
        searchContent(
          '/wiki',
          {
            'path.depth': 1,
            portal_type: ['Wiki'],
            review_state: 'discussion',
            departments: currentUserDepartments,
            metadata_fields: ['departments', 'modified'],
            b_size: 50,
            sort_on: 'modified',
            sort_order: 'reverse',
          },
          'discussionWikis',
        ),
      );
    }
  }, [dispatch, user.fachbereich]);

  const columns = {
    title: {
      label: 'Artikel',
      cellComponent: (entry) => {
        return entry['@id'] ? (
          <Link to={flattenToAppURL(entry['@id'])}>{entry.title}</Link>
        ) : (
          <></>
        );
      },
    },
    departments: {
      label: 'Bereich',
      cellComponent: (entry) => {
        const filteredDepartments =
          groups.filter((group) =>
            entry.departments?.includes(group.groupname),
          ) || [];

        return filteredDepartments.length > 0 ? (
          <>
            {filteredDepartments.map((department, departmentIndex) => {
              return (
                <Label
                  className={'department-label'}
                  title={department.title}
                  key={departmentIndex}
                  style={{ background: department.color }}
                >
                  {department.abbreviation}
                </Label>
              );
            })}
          </>
        ) : (
          <></>
        );
      },
    },
  };

  const columnsVerified = {
    ...columns,
    title: {
      ...columns.title,
      label: verifiedWikiItems.length + ' Artikel',
    },
    modified: {
      label: 'Änderungsdatum',
      cellComponent: (entry) => {
        return (
          <FormattedDate
            date={entry.modified}
            format={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
          />
        );
      },
    },
  };

  const columnsReleaseControl = {
    ...columns,
    title: {
      ...columns.title,
      label: releaseControlWikiItems.length + ' Artikel',
    },
    modified: {
      label: 'Änderungsdatum',
      cellComponent: (entry) => {
        return (
          <FormattedDate
            date={entry.modified}
            format={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
          />
        );
      },
    },
  };

  const columnsInDiscussion = {
    ...columns,
    title: {
      ...columns.title,
      label: discussionWikiItems.length + ' Artikel',
    },
    modified: {
      label: 'Änderungsdatum',
      cellComponent: (entry) => {
        return (
          <FormattedDate
            date={entry.modified}
            format={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
          />
        );
      },
    },
  };

  return (
    <>
      <List
        itemColumns={columnsVerified}
        items={verifiedWikiItems}
        batched={25}
        pagination={false}
        headColor={'#40d4e3'}
        headTitle={'Verifiziert'}
        sizeOptions={[1]}
        type={'Wiki'}
        sortColumn={'modified'}
      />
      <List
        itemColumns={columnsReleaseControl}
        items={releaseControlWikiItems}
        batched={25}
        pagination={false}
        headColor={'#ff4081'}
        headTitle={'Benötigt Abnahme'}
        sizeOptions={[1]}
        type={'Wiki'}
        sortColumn={'modified'}
      />
      <List
        itemColumns={columnsInDiscussion}
        items={discussionWikiItems}
        batched={25}
        pagination={false}
        headColor={'#29eac9'}
        headTitle={'Zur Diskussion'}
        sizeOptions={[1]}
        type={'Wiki'}
        sortColumn={'modified'}
      />
    </>
  );
};

export default DashboardWikis;
